import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { ASSETS } from "@/constants/site";
import { ArrowRight, Gavel, ClipboardCheck, Package, Truck, Ship } from "lucide-react";

const ICONS = [Gavel, ClipboardCheck, Package, Truck, Ship];

export default function ExportProcess() {
  const { t } = useLang();
  const images = [
    ASSETS.processAuction,
    ASSETS.processYard,
    ASSETS.processContainer,
    ASSETS.processTransport,
    ASSETS.processDelivery,
  ];

  return (
    <section id="process" className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <div className="section-label mb-5">{t.process.label}</div>
            <h2 className="font-heading uppercase text-white text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
              {t.process.title}
            </h2>
          </div>
          <p className="md:col-span-5 text-neutral-400 text-base leading-relaxed">
            {t.process.body}
          </p>
        </div>

        {/* Timeline steps */}
        <div className="relative">
          {/* Horizontal connector line (desktop only) */}
          <div className="hidden lg:block absolute top-[38px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F5B800]/40 to-transparent pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
            {t.process.steps.map((step, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex flex-col group"
                >
                  {/* Step marker on line */}
                  <div className="relative flex items-center justify-center mb-6">
                    <div className="w-[76px] h-[76px] bg-[#0A0A0A] border border-[#F5B800]/40 flex items-center justify-center relative z-10 group-hover:border-[#F5B800] transition-colors">
                      <Icon className="text-[#F5B800]" size={26} strokeWidth={1.8} />
                    </div>
                  </div>

                  {/* Step number */}
                  <div className="font-heading text-[#F5B800] text-2xl mb-3 text-center">
                    {step.n}
                  </div>

                  {/* Image */}
                  <div className="relative overflow-hidden border border-white/10 aspect-[4/3] mb-5">
                    <img
                      src={images[i]}
                      alt={step.t}
                      className="absolute inset-0 w-full h-full object-cover machine-img"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>

                  {/* Title + description */}
                  <div className="px-1">
                    <h3 className="font-heading uppercase text-white text-lg md:text-xl leading-tight">
                      {step.t}
                    </h3>
                    <p className="mt-3 text-neutral-400 text-xs leading-relaxed">
                      {step.d}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Ocean Freight — extended photo gallery */}
        <div className="mt-24 md:mt-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="section-label mb-4">{t.process.galleryLabel}</div>
              <h3 className="font-heading uppercase text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
                {t.process.galleryTitle}
              </h3>
            </div>
            <div className="hidden md:block text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              STEP 05 · GALLERY
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { img: ASSETS.freight1, meta: t.process.gallery[0] },
              { img: ASSETS.freight2, meta: t.process.gallery[1] },
              { img: ASSETS.freight3, meta: t.process.gallery[2] },
            ].map((g, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col bg-[#0F0F0F] border border-white/10 hover:border-[#F5B800]/40 transition-colors duration-300 group"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={g.img}
                    alt={g.meta.t}
                    className="absolute inset-0 w-full h-full object-cover machine-img"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1.5 bg-[#F5B800] text-black">
                      Shipped
                    </span>
                  </div>
                </div>
                <figcaption className="p-5 border-t border-white/10">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5B800] font-bold">
                    Ocean Freight
                  </div>
                  <div className="mt-2 font-heading uppercase text-white text-lg md:text-xl leading-tight">
                    {g.meta.t}
                  </div>
                  <p className="mt-2 text-neutral-400 text-xs leading-relaxed">
                    {g.meta.d}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 flex justify-center">
          <a
            href="#contact"
            className="btn-primary sharp inline-flex items-center gap-3"
          >
            Start Your Export
            <ArrowRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </section>
  );
}
